// Client-side mirror of server/db.js CONFIG_DEFAULTS, projected through
// /api/config. ConfigProvider mounts at the top of App and pre-fills with
// FALLBACK_CONFIG so first-paint never blocks. main.jsx kicks off a
// background fetch and updates the context once the network call lands.
//
// Components read config via useConfig(); see params.js, OnboardingFlow,
// AddTankFlow, Feedback, LogView, and HomeView.

import React, { createContext, useContext } from 'react';
import { DEFAULT_TASK_EXPLAINERS } from '../../shared/task-explainers.js';
import { DEFAULT_PARAM_EXPLAINERS } from '../../shared/param-explainers.js';

// In-code defaults — must mirror the public-keyed slice of db.js
// CONFIG_DEFAULTS so the SPA can render even before the network finishes
// (or if /api/config is unreachable, e.g. offline reload).
export const FALLBACK_CONFIG = {
  version: 0,
  tasks: { explainers: DEFAULT_TASK_EXPLAINERS },
  onboarding: {
    taxonomy: {
      intents: [
        { k: 'starting-new', label: 'Starting a new tank', sub: "I'm at the beginning" },
        { k: 'improving', label: 'Improving one I already have', sub: 'Something could be better' },
        { k: 'tracking', label: 'Tracking day-to-day', sub: 'I want a record' },
        { k: 'solving', label: 'Solving a problem right now', sub: 'Something is wrong' },
      ],
      water_types: [
        { k: 'saltwater', label: 'Saltwater' },
        { k: 'freshwater', label: 'Freshwater' },
        { k: 'unsure', label: "I'm not sure yet", only_intent: 'starting-new' },
      ],
      subtypes: {
        saltwater: [
          { k: 'sps', label: 'SPS-dominant', short_label: 'SPS-dominant', emoji: '🪸' },
          { k: 'mixed-reef', label: 'Mixed reef', short_label: 'Mixed reef', emoji: '🪸' },
          { k: 'soft-lps', label: 'Soft coral / LPS', short_label: 'Soft / LPS', emoji: '🌸' },
          { k: 'fowlr', label: 'Fish only (FOWLR)', short_label: 'FOWLR', emoji: '🐠' },
          {
            k: 'undecided-saltwater',
            label: "Haven't decided",
            short_label: 'Saltwater',
            emoji: '🌊',
            only_intent: 'starting-new',
          },
        ],
        freshwater: [
          {
            k: 'planted-high',
            label: 'Planted (high tech)',
            short_label: 'Planted (high tech)',
            emoji: '🌿',
          },
          {
            k: 'planted-low',
            label: 'Planted (low tech)',
            short_label: 'Planted (low tech)',
            emoji: '🌱',
          },
          { k: 'community', label: 'Community fish', short_label: 'Community', emoji: '🐟' },
          { k: 'cichlid', label: 'African cichlid', short_label: 'African cichlid', emoji: '🐡' },
          { k: 'shrimp', label: 'Shrimp', short_label: 'Shrimp', emoji: '🦐' },
          {
            k: 'other-freshwater',
            label: 'Something else',
            short_label: 'Freshwater',
            emoji: '🐟',
          },
        ],
      },
      sizes: [
        { k: '<20', label: 'Under 20 gallons', short_label: 'Under 20 gal', midpoint: 15 },
        { k: '20-40', label: '20 to 40', short_label: '20 to 40', midpoint: 30 },
        { k: '40-75', label: '40 to 75', short_label: '40 to 75', midpoint: 57 },
        { k: '75-125', label: '75 to 125', short_label: '75 to 125', midpoint: 100 },
        { k: '125-200', label: '125 to 200', short_label: '125 to 200', midpoint: 160 },
        { k: '200+', label: 'Over 200', short_label: 'Over 200', midpoint: 250 },
      ],
      ages: [
        { k: 'planning', label: 'Still planning', short_label: 'Still planning', days_ago: null },
        {
          k: '<1mo',
          label: 'Less than 1 month ago',
          short_label: 'Less than 1 month',
          days_ago: 15,
        },
        { k: '1-3mo', label: '1 to 3 months ago', short_label: '1 to 3 months ago', days_ago: 60 },
        { k: '3-6mo', label: '3 to 6 months ago', short_label: '3 to 6 months ago', days_ago: 135 },
        {
          k: '6-12mo',
          label: '6 to 12 months ago',
          short_label: '6 to 12 months ago',
          days_ago: 270,
        },
        { k: '1yr+', label: 'Over a year ago', short_label: 'Over a year ago', days_ago: 730 },
      ],
    },
    copy: {
      steps: ['welcome', 'intent', 'water', 'subtype', 'size', 'age', 'photo', 'first-read'],
      welcome: {
        h1: 'Every tank has a story.',
        h2_template: "Let's meet yours{{name_suffix}}.",
        cta: 'Begin',
      },
      intent: { h1: 'What brings you to TankSensei?' },
      water: { h1: 'Is it saltwater or freshwater?' },
      subtype: {
        saltwater_h1: 'What kind of reef — or is it fish-only?',
        freshwater_h1: "What's the style of tank?",
      },
      size: { h1: 'How big is the tank?' },
      age: { h1: 'When did you set it up?' },
      photo: {
        h1: 'One more thing.',
        body: 'Share a photo of your tank if you have one. I can often spot things from an image that take weeks to notice in person.',
        cta_with_photo: 'Let Sensei take a look',
        cta_without_photo: 'Skip for now',
      },
      loading_msg: 'Sensei is putting your tank together…',
      first_read_cta: 'Enter TankSensei',
    },
  },
  feedback: {
    kinds: [
      { k: 'bug', label: 'Bug', hint: 'Something is broken' },
      { k: 'idea', label: 'Idea', hint: 'Feature request or improvement' },
      { k: 'question', label: 'Question', hint: 'How do I…? or why does…?' },
    ],
    severities: [
      { k: 'nice-to-have', label: 'Nice', hint: 'Nice to have, no rush', dot: 'var(--text-muted)' },
      { k: 'annoying', label: 'Annoying', hint: 'Annoying but workable', dot: '#ffc166' },
      { k: 'blocking', label: 'Blocking', hint: 'Blocking me from using it', dot: 'var(--alert)' },
    ],
  },
  capture: {
    presets: {
      hanna: {
        title: 'Hanna Checker',
        subtitle: 'Photo of the LCD',
        default_param: 'alk',
        confidence: 94,
        hint: 'Point at the Checker LCD in daylight',
      },
      strip: {
        title: 'Colour strip / vial',
        subtitle: 'Photo vs the reference card',
        default_param: 'phosphate',
        confidence: 87,
        hint: 'Centre the vial on the grey square',
      },
      refract: {
        title: 'Refractometer',
        subtitle: 'Photo through the eyepiece',
        default_param: 'salinity',
        confidence: 98,
        hint: 'Hold the eyepiece steady, light behind the plate',
      },
    },
    methods_for_param: {
      salinity: ['manual', 'refract'],
      default_builtin: ['manual', 'hanna', 'strip'],
      default_custom: ['manual'],
    },
  },
  params: {
    explainers: DEFAULT_PARAM_EXPLAINERS,
    short_labels: {
      alk: 'ALK',
      calcium: 'Ca',
      magnesium: 'Mg',
      nitrate: 'NO₃',
      phosphate: 'PO₄',
      salinity: 'SAL',
      temp: '°F',
      ph: 'pH',
    },
    baseline: {
      alk: { label: 'Alkalinity', unit: 'dKH', target: 8.5, acceptable: [8.0, 9.0], order: 10 },
      calcium: { label: 'Calcium', unit: 'ppm', target: 430, acceptable: [410, 450], order: 20 },
      magnesium: {
        label: 'Magnesium',
        unit: 'ppm',
        target: 1340,
        acceptable: [1300, 1400],
        order: 30,
      },
      nitrate: { label: 'Nitrate', unit: 'ppm', target: 5, acceptable: [1, 10], order: 40 },
      phosphate: {
        label: 'Phosphate',
        unit: 'ppm',
        target: 0.04,
        acceptable: [0.02, 0.08],
        order: 50,
      },
      salinity: {
        label: 'Salinity',
        unit: 'ppt',
        target: 35.0,
        acceptable: [33.5, 36.5],
        order: 60,
      },
      temp: { label: 'Temperature', unit: '°F', target: 78.0, acceptable: [77.5, 78.5], order: 70 },
      ph: { label: 'pH', unit: '', target: 8.1, acceptable: [7.9, 8.3], order: 80 },
    },
  },
  // Equipment catalog falls back to a marker shape so the picker
  // renders SOMETHING even when /api/config is unreachable on first
  // paint. The real catalog comes from the bundle (admin-editable).
  // The shape here keeps the section-grid + Other tile alive.
  equipment: {
    sections: [{ title: 'Other', kinds: ['other'] }],
    kinds: {
      other: { label: 'Other equipment', icon: '🔧', brands: [], subtypes: [], parts: [] },
    },
    part_kinds: { other: { label: 'Other' } },
  },
};

const ConfigCtx = createContext(FALLBACK_CONFIG);

export function ConfigProvider({ value, children }) {
  return React.createElement(ConfigCtx.Provider, { value: value || FALLBACK_CONFIG }, children);
}

export function useConfig() {
  return useContext(ConfigCtx);
}

// Cache the last-good fetch in localStorage so a brief network blip on a
// later page-load still produces a usable bundle. 7-day TTL — old enough
// to absorb hiccups, short enough to pick up admin edits eventually.
const CACHE_KEY = 'aqs:config';
const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export function readCachedConfig() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { stored_at, value } = JSON.parse(raw);
    if (!stored_at || Date.now() - stored_at > CACHE_TTL_MS) return null;
    return value;
  } catch {
    return null;
  }
}

export function writeCachedConfig(value) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ stored_at: Date.now(), value }));
  } catch {
    /* quota or unavailable; non-fatal */
  }
}

export async function fetchConfig() {
  const r = await fetch('/api/config', { credentials: 'omit' });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return await r.json();
}
